import type { Color, PaletteColor, PaletteColorOptions, ThemeOptions } from '@mui/material';
import { common } from '@mui/material/colors';

declare module '@mui/material/styles' {
  interface Palette {
    infoButton?: PaletteColor;
    neutral?: PaletteColor;
    border?: PaletteColor;
    red?: Partial<Color>;
    alert?: {
      info?: PaletteColor;
      warning?: PaletteColor;
      success?: PaletteColor;
      error?: PaletteColor;
    };
    chip?: {
      info?: PaletteColor;
      warning?: PaletteColor;
      success?: PaletteColor;
      error?: PaletteColor;
    };
    slate?: Partial<Color>;
    link?: string;
  }

  interface PaletteOptions {
    infoButton?: PaletteColorOptions;
    neutral?: PaletteColorOptions;
    border?: PaletteColorOptions;
    alert?: {
      info?: PaletteColorOptions;
      warning?: PaletteColorOptions;
      success?: PaletteColorOptions;
      error?: PaletteColorOptions;
    };
    chip?: {
      info?: PaletteColorOptions;
      warning?: PaletteColorOptions;
      success?: PaletteColorOptions;
      error?: PaletteColorOptions;
    };
    red?: Partial<Color>;
    slate?: Partial<Color>;
    link?: string;
  }
}

export const palette = {
  primary: {
    main: '#162395',
    light: '#DCDEEF',
    dark: '#0D1559',
    contrastText: '#fff',
  },
  secondary: {
    main: '#E0E0E0',
    light: '#F0F0F5',
    dark: '#292A33',
    contrastText: '#292A33',
  },
  error: {
    main: '#E82E00',
    light: '#EF5350',
    dark: '#B52400',
    contrastText: '#fff',
  },
  warning: {
    main: '#FFA726',
    light: '#FFB74D',
    dark: '#E76F00',
    contrastText: '#fff',
  },
  info: {
    main: '#0288D1',
    light: '#03A9F4',
    dark: '#01579B',
    contrastText: '#fff',
  },
  success: {
    main: '#33CC00',
    light: '#96D700',
    dark: '#249100',
    contrastText: '#fff',
  },
  background: {
    paper: '#fff',
    default: '#fff',
  },
  border: {
    main: '#D9D9D9',
    light: 'rgba(0, 0, 0, 0.12)',
  },
  common,
  red: {
    50: '#FFEBE6',
    100: '#FFC0B0',
    200: '#FFA18A',
    300: '#FF7654',
    400: '#FF5C33',
    500: '#FF3300',
    600: '#E82E00',
    700: '#B52400',
    800: '#8C1C00',
    900: '#6B1500',
  },

  infoButton: {
    main: '#292A33',
    contrastText: '#fff',
  },

  neutral: {
    main: '#64748B',
    light: '#64748B',
    contrastText: '#fff',
  },

  contrastThreshold: 3,
  tonalOffset: 0.2,
  divider: '#D0D1D9',
  link: '#3944A5',

  text: {
    primary: '#292A33',
    secondary: '#666666',
    disabled: '#9B9DB0',
  },

  action: {
    active: '#64667D',
    hover: '#F0F0F5',
    hoverOpacity: 0.04,
    selected: '#F0F0F5',
    selectedOpacity: 0.08,
    disabled: '#9B9DB0',
    disabledBackground: '#F0F0F5',
    disabledOpacity: 0.38,
    focus: '#F0F0F5',
    focusOpacity: 0.12,
    activatedOpacity: 0.12,
  },

  slate: {
    900: '#292A33',
    700: '#4E4F61',
    500: '#71738A',
    300: '#9B9DB0',
    50: '#FAFAFA',
  },

  alert: {
    info: {
      main: '#DCDEEF',
      contrastText: '#0D1559',
    },
    warning: {
      main: '#FFF5E6',
      contrastText: '#E76F00',
    },
    success: {
      main: '#EBFAE6',
      contrastText: '#249100',
    },
    error: {
      main: '#FFEBE6',
      contrastText: '#B52400',
      dark: '#541313',
    },
  },

  chip: {
    info: {
      main: '#DCDEEF',
      contrastText: '#0D1559',
    },
    warning: {
      main: '#FFF5E6',
      contrastText: '#E76F00',
    },
    success: {
      main: '#EBFAE6',
      contrastText: '#249100',
    },
    error: {
      main: '#FFEBE6',
      contrastText: '#B52400',
    },
  },
} as const satisfies NonNullable<ThemeOptions['palette']>;
